import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { styled, alpha } from '@mui/material/styles';
import { Box, Link, Drawer, Typography, Avatar, Button } from '@mui/material';
import Iconify from '../../../components/iconify';


import useResponsive from '../../../hooks/useResponsive';
// components
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
//
import navConfig from './config';
import { decrypt } from '../../../utils/crypto';
import { usePermissions } from '../../../context/PermissionsContext';


// ----------------------------------------------------------------------

const NAV_WIDTH = 280;

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

// ----------------------------------------------------------------------

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav }) {
  const { pathname } = useLocation();
  const usuario = decrypt(sessionStorage.getItem('user'));
  const rolName = decrypt(sessionStorage.getItem('rolName'));
  const { hasPermission, isAdmin } = usePermissions();

  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Filtrar opciones del menú según permisos del usuario
  const filteredNavConfig = useMemo(() => {
    return navConfig
      .map((item) => {
        // Ítem con children → filtrar cada child y devolver el grupo si queda alguno
        if (item.children) {
          const visibleChildren = item.children.filter((child) => {
            if (child.adminOnly) return isAdmin;
            if (child.hideForAdmin && isAdmin) return false;
            if (child.hideForRoles && child.hideForRoles.includes(rolName)) return false;
            if (child.showForRoles && child.showForRoles.includes(rolName)) return true;
            if (child.showForRoles && !child.showForRoles.includes(rolName)) return false;
            if (child.requiredPermissions?.length > 0) {
              if (isAdmin) return true;
              return child.requiredPermissions.some((p) => hasPermission(p));
            }
            return true;
          });

          if (visibleChildren.length === 0) return null;
          if (item.adminOnly && !isAdmin) return null;
          if (item.hideForAdmin && isAdmin) return null;
          return { ...item, children: visibleChildren };
        }

        // Ítem hoja
        if (item.adminOnly) return isAdmin ? item : null;
        if (item.hideForAdmin && isAdmin) return null;
        if (item.hideForRoles && item.hideForRoles.includes(rolName)) return null;
        if (item.showForRoles && item.showForRoles.includes(rolName)) return item;
        if (item.showForRoles && !item.showForRoles.includes(rolName)) return null;
        if (item.requiredPermissions?.length > 0) {
          if (isAdmin) return item;
          return item.requiredPermissions.some((p) => hasPermission(p)) ? item : null;
        }
        return item; // Sin restricciones → siempre visible
      })
      .filter(Boolean);
  }, [isAdmin, hasPermission, rolName]);


  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
      style={{ background: '#fff' }}
    >
      <Box sx={{ px: 2.5, py: 3, display: 'inline-flex' }}>
        <img src="/img/logo.png" alt="Logo" style={{ borderRadius: '10px' }} />
      </Box>

      <Box sx={{ mb: 5, mx: 2.5 }}>
        <Link underline="none">
          <StyledAccount>
            <Avatar sx={{ bgcolor: 'primary.main' }}>{usuario?.charAt(0).toUpperCase()}</Avatar>

            <Box sx={{ ml: 2, flexGrow: 1 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                {usuario}
              </Typography>

              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {rolName}
              </Typography>
            </Box>
          </StyledAccount>
        </Link>

        <Button
          fullWidth
          variant="outlined"
          color="error"
          component={Link}
          href="/"
          onClick={() => sessionStorage.clear()}
          startIcon={<Iconify icon="eva:log-out-fill" />}
          sx={{
            mt: 2,
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
            justifyContent: 'flex-start',
            px: 2,
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.2),
            },
          }}
        >
          Cerrar sesión
        </Button>

      </Box>


      <Typography variant="overline" sx={{ px: 5, color: 'text.disabled', display: 'block', mb: 1 }}>
        Menú
      </Typography>
      <NavSection data={filteredNavConfig} />


      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV_WIDTH },
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              bgcolor: 'background.default',
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: { width: NAV_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
